import React, { FC, ReactNode, createContext, memo, useContext, useMemo, useState, Dispatch, useRef } from 'react';
import { getCvAccessToken } from '@carvana/core-auth';
import { datadogRum } from '@datadog/browser-rum';
import { getApiBaseUrl } from '@components/service-and-repair/services/base-url-config';
import { SarStore } from '@store/sar-store';
import { Category, SubCategory, SubSubCategory, Location } from '../../constants';
import { CoverageScreenType } from '../../components/coverage-screen/get-logic/coverage-type';

const noop = () => {};

export type UserSelectionsType = {
  category?: Category;
  subcategory?: SubCategory;
  subsubcategory?: SubSubCategory;
  location?: Location;
  mileage?: number;
  media?: File[];
  details?: string;
};

export type CoverageMetadata = {
  coverageText?: string;
  coverageType?: string;
};

// Backend wire values persisted in AfterSaleRepair; distinct from CoverageScreenType
export const CoverageTypeSentinel = {
  cosmeticReview: 'cosmeticReview',
  notCovered: 'notCovered',
} as const;

export const UserSelectionsDefaults = {
  userSelections: {} as UserSelectionsType,
  updateUserSelections: noop as (newSelections?: Partial<UserSelectionsType>) => void,
  userFeedData: {} as any,
  setUserFeedData: noop as Dispatch<React.SetStateAction<any>>,
  saveUserSelections: noop as (coverageScreen: CoverageScreenType, metadata?: CoverageMetadata) => any,
  clearUserSelections: noop as () => void,
  hasUploadError: false as boolean,
  setHasUploadError: noop as Dispatch<React.SetStateAction<boolean>>,
  userSelectionsSubmitted: false as boolean,
  setUserSelectionsSubmitted: noop as Dispatch<React.SetStateAction<boolean>>,
  asrId: null as number | null,
  setAsrId: noop as Dispatch<React.SetStateAction<number | null>>,
};

export const UserSelectionsContext = createContext(UserSelectionsDefaults);

export const useUserSelections = () => {
  const context = useContext(UserSelectionsContext);
  if (context === undefined) {
    throw new Error('useUserSelections must be used within a UserSelectionsProvider');
  }

  return context;
};

type UserSelectionsProviderProps = {
  children: ReactNode | ReactNode[];
};

export const UserSelectionsProvider: FC<UserSelectionsProviderProps> = memo(({ children }: any) => {
  const [userSelections, setUserSelections] = useState<UserSelectionsType>({});
  const [userFeedData, setUserFeedData] = useState({});
  const [hasUploadError, setHasUploadError] = useState(false);
  const [userSelectionsSubmitted, setUserSelectionsSubmitted] = useState<boolean>(false);
  const [asrId, setAsrId] = useState<number | null>(null);

  const updateUserSelections = (newSelections?: Partial<UserSelectionsType>) => {
    if (!newSelections) return;
    let resetSelections = {};

    if (newSelections?.category) {
      resetSelections = {
        subcategory: undefined,
        subsubcategory: undefined,
        location: undefined,
      };
    }

    if (newSelections?.subcategory) {
      resetSelections = {
        subsubcategory: undefined,
        location: undefined,
      };
    }

    setUserSelections((prevSelections) => ({
      ...prevSelections,
      ...newSelections,
      ...resetSelections,
    }));
  };

  const clearUserSelections = () => {
    setUserSelections({});
    setAsrId(null);
  };

  const saveUserSelections = async (coverageScreen: CoverageScreenType, metadata?: CoverageMetadata) => {
    const ddAction = 'sar-save-user-selections';
    const startTime = Date.now();

    try {
      datadogRum.addAction(ddAction, { type: 'start', sarSessionId: SarStore.sarSessionId });
      const accessToken = getCvAccessToken();

      const baseUrl = getApiBaseUrl();
      const { vehicleId, mileage: saleMileage, vehicle } = userFeedData || ({} as any);
      const { category, subcategory, subsubcategory, location, media, details } = userSelections;

      const formData = new FormData();
      formData.append(`category`, category || '');
      formData.append(`subcategory`, subcategory || '');
      if (subsubcategory !== SubSubCategory.TireDamageYes && subsubcategory !== SubSubCategory.TireDamageNo) {
        const subsubcategoryValue = subcategory === SubCategory.Glass ? location || '' : subsubcategory || '';
        formData.append(`subsubcategory`, subsubcategoryValue);
      }
      formData.append(`location`, location || '');
      formData.append('mileage', userSelections?.mileage?.toString() || saleMileage?.toString() || '');
      if (details) {
        formData.append('details', details);
      }
      (media || []).forEach((image: any, index: number) => {
        formData.append('media', image);
      });

      const resolvedVehicleId = vehicleId || vehicle?.vehicleId;
      if (!resolvedVehicleId) {
        datadogRum.addAction(ddAction, {
          type: 'error',
          error: { message: 'Vehicle ID is required for service request submission' },
          sarSessionId: SarStore.sarSessionId,
        });
        throw new Error('Vehicle ID is required for service request submission');
      }
      formData.append('vehicleId', resolvedVehicleId);

      formData.append('coverageScreenShown', coverageScreen);
      if (metadata?.coverageText) {
        formData.append('coverageText', metadata.coverageText);
      }
      if (metadata?.coverageType) {
        formData.append('coverageType', metadata.coverageType);
      }

      const response = await fetch(`${baseUrl}/op/mycarvana/api/v1/serviceandrepair/saveuserselections`, {
        method: 'POST',
        headers: {
          Accept: 'application/json, text/plain, */*',
          credentials: 'include',
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      const data = await response.json();
      const elapsedMs = Date.now() - startTime;

      if (!response.ok) {
        datadogRum.addAction(ddAction, {
          type: 'error',
          error: {
            responseData: data,
            responseStatus: response.status,
            responseStatusText: response.statusText,
            responseType: response.type,
            responseBody: response.body,
          },
          sarSessionId: SarStore.sarSessionId,
          elapsedMs,
        });
        return null;
      }

      datadogRum.addAction(ddAction, { type: 'success', sarSessionId: SarStore.sarSessionId, elapsedMs });

      if (data?.content?.asrId !== undefined && data?.content?.asrId !== null) {
        setAsrId(data.content.asrId);
      } else {
        datadogRum.addAction('sar-save-user-selections-missing-asrId', {
          type: 'warning',
          message: 'saveUserSelections response did not include asrId',
          responseData: data,
          sarSessionId: SarStore.sarSessionId,
        });
      }

      return data;
    } catch (error: unknown) {
      const elapsedMs = Date.now() - startTime;

      if (error instanceof Error) {
        datadogRum.addAction(ddAction, {
          type: 'error',
          error: {
            name: error.name,
            message: error.message,
            stack: error.stack,
          },
          message: error.message,
          sarSessionId: SarStore.sarSessionId,
          elapsedMs,
        });
      } else {
        datadogRum.addAction(ddAction, {
          type: 'error',
          error: typeof error === 'object' && error !== null ? JSON.stringify(error) : String(error),
          message: 'Unknown Error saving user selections',
          sarSessionId: SarStore.sarSessionId,
          elapsedMs,
        });
      }
    }
  };

  const value = useMemo(
    () => ({
      userSelections,
      userFeedData,
      setUserFeedData,
      saveUserSelections,
      updateUserSelections,
      clearUserSelections,
      hasUploadError,
      setHasUploadError,
      userSelectionsSubmitted,
      setUserSelectionsSubmitted,
      asrId,
      setAsrId,
    }),
    [userSelections, userFeedData, hasUploadError, userSelectionsSubmitted, asrId]
  );

  return <UserSelectionsContext.Provider value={value}>{children}</UserSelectionsContext.Provider>;
});
